import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ExternalLink, Loader2, Plus, ShieldAlert, Trash2 } from "lucide-react";
import { DetailSkeleton } from "@/components/marketplace";
import { Select } from "@/components/Select";
import { useTranslation } from "@/hooks/useTranslation";
import {
  useMyCreatorProfile,
  useSocialAccounts,
  useUpsertCreatorProfile,
  type SocialPlatform,
} from "@/hooks/useMarketplace";
import { COUNTRIES, COUNTRY_LABELS, NICHES, PLATFORMS, PLATFORM_LABELS } from "./constants";

const INPUT_CLASS =
  "w-full px-3 py-2.5 rounded-control border border-neutral-200 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-steel-600/20 focus:border-steel-600/40";

const LABEL_CLASS = "block text-xs font-medium text-neutral-700 mb-1.5";

export default function CreatorProfileEditor() {
  const { t } = useTranslation();
  const { profile, loading } = useMyCreatorProfile();
  const { save, loading: saving } = useUpsertCreatorProfile();
  const { saveAccount, deleteAccount, saving: savingAccount } = useSocialAccounts();

  const [form, setForm] = useState({
    handle: "",
    displayName: "",
    bio: "",
    country: "UY",
    city: "",
    minRate: "",
    acceptsGifting: true,
    isPublished: false,
  });
  const [niches, setNiches] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newAccount, setNewAccount] = useState({
    platform: "INSTAGRAM" as SocialPlatform,
    handle: "",
    followers: "",
    engagementRate: "",
  });

  // Hydrate once the profile arrives. Guarded on `profile` rather than run on
  // every render so in-progress edits are never clobbered by a refetch.
  useEffect(() => {
    if (!profile) return;
    setForm({
      handle: profile.handle,
      displayName: profile.displayName,
      bio: profile.bio ?? "",
      country: profile.country ?? "UY",
      city: profile.city ?? "",
      minRate: profile.minRate !== null ? String(profile.minRate) : "",
      acceptsGifting: profile.acceptsGifting,
      isPublished: profile.isPublished,
    });
    setNiches(profile.niches);
  }, [profile]);

  const flashSaved = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const onSave = async () => {
    setError(null);
    try {
      await save({
        handle: form.handle,
        displayName: form.displayName,
        bio: form.bio || null,
        country: form.country,
        city: form.city || null,
        niches,
        minRate: form.minRate ? Number(form.minRate) : null,
        acceptsGifting: form.acceptsGifting,
        isPublished: form.isPublished,
      });
      flashSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const onAddAccount = async () => {
    if (!newAccount.handle.trim()) return;
    setError(null);
    try {
      await saveAccount({
        platform: newAccount.platform,
        handle: newAccount.handle,
        followers: newAccount.followers ? Number(newAccount.followers) : undefined,
        engagementRate: newAccount.engagementRate
          ? Number(newAccount.engagementRate)
          : undefined,
      });
      setNewAccount({ platform: "INSTAGRAM", handle: "", followers: "", engagementRate: "" });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  if (loading && !profile) {
    return (
      <div className="adv-page">
        <DetailSkeleton />
      </div>
    );
  }

  const canPublish = Boolean(
    form.handle && form.displayName && form.country && (profile?.socialAccounts.length ?? 0) > 0,
  );

  return (
    <div className="max-w-2xl mx-auto px-6 py-10">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold text-neutral-900">{t("marketplace.profile.title")}</h1>
          <p className="text-sm text-neutral-500 mt-1">{t("marketplace.profile.subtitle")}</p>
        </div>
        {profile?.isPublished && (
          <Link
            to={`/creadores/${profile.handle}`}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-steel-600 hover:text-steel-700 flex-shrink-0 mt-1"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            {t("marketplace.profile.viewPublic")}
          </Link>
        )}
      </div>

      {error && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-control px-3 py-2 mb-4">
          {error}
        </p>
      )}

      <div className="bg-white border border-neutral-200 rounded-card p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLASS}>{t("marketplace.profile.displayName")}</label>
            <input
              className={INPUT_CLASS}
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              placeholder={t("marketplace.profile.displayNamePlaceholder")}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>{t("marketplace.profile.handle")}</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400">
                @
              </span>
              <input
                className={`${INPUT_CLASS} pl-7`}
                value={form.handle}
                onChange={(e) => setForm({ ...form, handle: e.target.value })}
                placeholder="tu-nombre"
              />
            </div>
          </div>
        </div>

        <div>
          <label className={LABEL_CLASS}>{t("marketplace.profile.bio")}</label>
          <textarea
            className={`${INPUT_CLASS} resize-none`}
            rows={3}
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            placeholder={t("marketplace.profile.bioPlaceholder")}
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLASS}>{t("marketplace.profile.country")}</label>
            <Select
              value={form.country}
              onChange={(v) => setForm({ ...form, country: v })}
              options={COUNTRIES.map((c) => ({ value: c, label: COUNTRY_LABELS[c] }))}
              size="md"
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>{t("marketplace.profile.city")}</label>
            <input
              className={INPUT_CLASS}
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
            />
          </div>
        </div>

        <div>
          <label className={LABEL_CLASS}>{t("marketplace.profile.niches")}</label>
          <div className="flex flex-wrap gap-1.5">
            {NICHES.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() =>
                  setNiches(niches.includes(n) ? niches.filter((x) => x !== n) : [...niches, n])
                }
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors cursor-pointer ${
                  niches.includes(n)
                    ? "bg-steel-600 border-steel-600 text-white"
                    : "bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300"
                }`}
              >
                {t(`marketplace.niches.${n}`, { defaultValue: n })}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 items-end">
          <div>
            <label className={LABEL_CLASS}>{t("marketplace.profile.minRate")}</label>
            <input
              className={INPUT_CLASS}
              type="number"
              min="0"
              value={form.minRate}
              onChange={(e) => setForm({ ...form, minRate: e.target.value })}
              placeholder="0"
            />
            <p className="text-[11px] text-neutral-400 mt-1">
              {t("marketplace.profile.minRateHint")}
            </p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer pb-1">
            <input
              type="checkbox"
              checked={form.acceptsGifting}
              onChange={(e) => setForm({ ...form, acceptsGifting: e.target.checked })}
              className="w-4 h-4 rounded border-neutral-300 text-steel-600 focus:ring-steel-600/20"
            />
            <span className="text-sm text-neutral-700">
              {t("marketplace.profile.acceptsGifting")}
            </span>
          </label>
        </div>
      </div>

      {/* Social accounts */}
      <div className="bg-white border border-neutral-200 rounded-card p-6 mt-4">
        <h2 className="text-sm font-semibold text-neutral-900">
          {t("marketplace.profile.accountsTitle")}
        </h2>
        <p className="flex items-start gap-1.5 text-xs text-amber-700 mt-1.5">
          <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
          {t("marketplace.profile.accountsNotice")}
        </p>

        <div className="mt-4 space-y-2">
          {profile?.socialAccounts.map((a) => (
            <div
              key={a.id}
              className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-control border border-neutral-100 bg-neutral-50"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-neutral-900">
                  {PLATFORM_LABELS[a.platform]}{" "}
                  <span className="text-neutral-400 font-normal">@{a.handle}</span>
                </p>
                <p className="text-xs text-neutral-500">
                  {a.followers?.toLocaleString("es") ?? "—"} {t("marketplace.creator.followers")}
                  {a.engagementRate !== null && ` · ${a.engagementRate}%`}
                  {" · "}
                  {a.verified
                    ? t("marketplace.creator.verified")
                    : t("marketplace.creator.selfReported")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => deleteAccount(a.id)}
                className="text-neutral-400 hover:text-red-600 transition-colors cursor-pointer flex-shrink-0"
                aria-label={t("common.delete")}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {profile && (
          <div className="mt-4 pt-4 border-t border-neutral-100 grid sm:grid-cols-4 gap-2 items-end">
            <Select
              value={newAccount.platform}
              onChange={(v) => setNewAccount({ ...newAccount, platform: v as SocialPlatform })}
              options={PLATFORMS.map((p) => ({ value: p, label: PLATFORM_LABELS[p] }))}
              size="md"
            />
            <input
              className={INPUT_CLASS}
              value={newAccount.handle}
              onChange={(e) => setNewAccount({ ...newAccount, handle: e.target.value })}
              placeholder={t("marketplace.profile.accountHandle")}
            />
            <input
              className={INPUT_CLASS}
              type="number"
              min="0"
              value={newAccount.followers}
              onChange={(e) => setNewAccount({ ...newAccount, followers: e.target.value })}
              placeholder={t("marketplace.creator.followers")}
            />
            <button
              type="button"
              onClick={onAddAccount}
              disabled={savingAccount || !newAccount.handle.trim()}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-control border border-neutral-200 text-sm font-medium text-neutral-700 hover:border-neutral-300 hover:bg-neutral-50 transition-all cursor-pointer disabled:opacity-40"
            >
              <Plus className="w-4 h-4" />
              {t("common.add")}
            </button>
          </div>
        )}

        {!profile && (
          <p className="text-xs text-neutral-400 mt-3">
            {t("marketplace.profile.saveFirstHint")}
          </p>
        )}
      </div>

      {/* Publish + save */}
      <div className="bg-white border border-neutral-200 rounded-card p-6 mt-4 flex items-center justify-between gap-4">
        <label
          className={`flex items-center gap-2 ${canPublish ? "cursor-pointer" : "opacity-50"}`}
        >
          <input
            type="checkbox"
            disabled={!canPublish}
            checked={form.isPublished}
            onChange={(e) => setForm({ ...form, isPublished: e.target.checked })}
            className="w-4 h-4 rounded border-neutral-300 text-steel-600 focus:ring-steel-600/20"
          />
          <span className="text-sm text-neutral-700">
            {t("marketplace.profile.publish")}
            {!canPublish && (
              <span className="block text-[11px] text-neutral-400">
                {t("marketplace.profile.publishBlocked")}
              </span>
            )}
          </span>
        </label>

        <button
          type="button"
          onClick={onSave}
          disabled={saving || !form.handle || !form.displayName}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-button bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800 transition-all cursor-pointer disabled:opacity-40 flex-shrink-0"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          {saved && <Check className="w-4 h-4" />}
          {saved ? t("common.saved") : t("common.save")}
        </button>
      </div>
    </div>
  );
}
